import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getUploadsUrl } from '../services/api';
import './SearchSelect.css';
import './BornePicker.css';

/**
 * Choix d'un contact, présenté comme le sélecteur de borne :
 *   [photo] Prénom Nom – Ville
 * Recherche sur le nom, le prénom et la ville ; plusieurs mots, dans
 * n'importe quel ordre, sans tenir compte des accents.
 *
 * Props : contacts ({ id, prenom, nom, photo, civilite, antenne_ville }),
 * value (id en texte), onChange(id en texte).
 */

// Qualification « Antenne actuelle » (cf. /lookups/qualifications).
const CURRENT_QUALIFICATION = 'antenne_principale';

const normalize = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const nameOf = (c) => [c.prenom, c.nom].map(s => (s || '').trim()).filter(Boolean).join(' ');
const avatarOf = (c) => (c.photo
  ? getUploadsUrl(c.photo)
  : (c.civilite === 'm' ? '/img/avatar-homme.svg' : '/img/avatar-femme.svg'));

export default function ContactPicker({ contacts, value, onChange, placeholder = 'Rechercher un contact' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selected = useMemo(() => contacts.find(c => String(c.id) === String(value)), [contacts, value]);

  // Par défaut, seules les antennes actuelles sont proposées ; la case
  // permet d'afficher tous les contacts.
  const [onlyCurrent, setOnlyCurrent] = useState(true);

  const filtered = useMemo(() => {
    const words = normalize(query).split(/\s+/).filter(Boolean);
    const list = contacts.filter(c => {
      if (onlyCurrent && c.qualification !== CURRENT_QUALIFICATION) return false;
      if (words.length === 0) return true;
      const hay = normalize(`${c.prenom} ${c.nom} ${c.antenne_ville}`);
      return words.every(w => hay.includes(w));
    });
    return [...list].sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'fr'));
  }, [contacts, query, onlyCurrent]);

  const pick = (c) => {
    onChange(c ? String(c.id) : '');
    setOpen(false);
    setQuery('');
  };

  return (
    <div className="search-select borne-picker" ref={ref}>
      <div className={`search-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
        {selected ? (
          <span className="search-select-value">
            <span>{nameOf(selected)}{selected.antenne_ville ? ` – ${selected.antenne_ville}` : ''}</span>
            <button type="button" className="search-select-clear" onClick={(e) => { e.stopPropagation(); pick(null); }}>&times;</button>
          </span>
        ) : (
          <span className="search-select-placeholder">{placeholder}</span>
        )}
        <svg className="search-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="search-select-dropdown bp-dropdown">
          <div className="search-select-input-wrapper">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Nom, prénom ou ville…"
              className="search-select-input"
            />
          </div>
          <label className="cp-filter">
            <input type="checkbox" checked={onlyCurrent} onChange={(e) => setOnlyCurrent(e.target.checked)} />
            Antennes actuelles seulement
          </label>
          <div className="bp-results">
            {filtered.length === 0 && (
              <div className="search-select-empty">
                Aucun contact{onlyCurrent ? ' parmi les antennes actuelles' : ''}
              </div>
            )}
            {filtered.map(c => (
              <div
                key={c.id}
                className={`bp-option${String(c.id) === String(value) ? ' selected' : ''}`}
                onClick={() => pick(c)}
              >
                <img src={avatarOf(c)} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                <span className="bp-contact-name">{nameOf(c)}</span>
                {c.antenne_ville && <span className="bp-contact-ville">– {c.antenne_ville}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
